import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  Button, 
  Space, 
  Tag, 
  Input, 
  Select, 
  message, 
  Modal, 
  Tooltip,
  Row,
  Col,
  Typography,
  Divider,
  Empty
} from 'antd';
import { 
  PlusOutlined, 
  SyncOutlined, 
  PhoneOutlined, 
  EnvironmentOutlined,
  ToolOutlined,
  CalendarOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined
} from '@ant-design/icons';
import {
  subscribeToSchedules,
  getUnconfirmedSchedules,
  updateScheduleStatus,
  deleteSchedule,
  syncSchedulesFromInvoices,
  getScheduleStats,
  JOB_TYPES,
  JOB_TYPE_LABELS,
  SCHEDULE_STATUSES,
  SCHEDULE_STATUS_LABELS,
  SCHEDULE_STATUS_COLORS
} from '../../firebase/scheduleService';
import { 
  getSingaporeDate, 
  getStandardizedDisplayDate,
  formatDateWithDay,
  formatDateShort,
  getDayName,
  isToday,
  isPastDate
} from '../../utils/dateUtils';
import JobScheduleForm from './JobScheduleForm';
import './JobScheduleList.css';

const { Option } = Select;
const { Title, Text } = Typography;

const JobScheduleList = ({ dateRange, onDateRangeChange }) => {
  const [schedules, setSchedules] = useState([]);
  const [unconfirmedSchedules, setUnconfirmedSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [autoSyncing, setAutoSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // 'active', 'all', 'completed'
  const [jobTypeFilter, setJobTypeFilter] = useState('all');
  const [filteredSchedules, setFilteredSchedules] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [stats, setStats] = useState({});
  const [schedulesUnsubscribe, setSchedulesUnsubscribe] = useState(null);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncStatus, setSyncStatus] = useState('never'); // 'never', 'syncing', 'success', 'error'


  // Sync metadata management
  const SYNC_STORAGE_KEY = 'scheduleLastSync';
  const SYNC_THRESHOLD_MINUTES = 30;

  // Get last sync time from localStorage
  const getLastSyncTime = useCallback(() => {
    try {
      const stored = localStorage.getItem(SYNC_STORAGE_KEY);
      return stored ? new Date(stored) : null;
    } catch (error) {
      console.error('Error reading sync timestamp:', error);
      return null;
    }
  }, []);

  // Save sync time to localStorage
  const saveLastSyncTime = useCallback((timestamp = new Date()) => {
    try {
      localStorage.setItem(SYNC_STORAGE_KEY, timestamp.toISOString());
      setLastSyncTime(timestamp);
    } catch (error) {
      console.error('Error saving sync timestamp:', error);
    }
  }, []);

  // Check if sync is needed based on timestamp
  const isSyncNeeded = useCallback((scheduleCount = 0) => {
    // Always sync if no schedules exist (empty state)
    if (scheduleCount === 0) {
      console.log('🔄 Auto-sync needed: Empty state detected');
      return true;
    }

    // Check timestamp-based sync
    const lastSync = getLastSyncTime();
    if (!lastSync) {
      console.log('🔄 Auto-sync needed: Never synced before');
      return true;
    }

    const minutesSinceSync = (new Date() - lastSync) / (1000 * 60);
    const isStale = minutesSinceSync > SYNC_THRESHOLD_MINUTES;
    
    if (isStale) {
      console.log(`🔄 Auto-sync needed: ${Math.round(minutesSinceSync)} minutes since last sync`);
    }
    
    return isStale;
  }, [getLastSyncTime]);

  // Load schedules with real-time updates
  const loadSchedules = useCallback(() => {
    console.log('🔄 Setting up schedule subscription with filter:', statusFilter);

    if (schedulesUnsubscribe) {
      console.log('🔄 Cleaning up previous subscription');
      schedulesUnsubscribe();
    }

    const options = {
      includePastDates: statusFilter === 'all' || statusFilter === 'completed'
    };

    if (statusFilter === 'completed') {
      options.status = SCHEDULE_STATUSES.COMPLETED;
    } else if (statusFilter === 'active') {
      // Include all unconfirmed schedules + confirmed schedules with future dates
      options.includeAllUnconfirmed = true;
      options.includePastDates = false;
    }

    console.log('📅 Subscription options:', options);

    const unsubscribe = subscribeToSchedules((schedulesData) => {
      console.log('📅 Received schedules data:', schedulesData.length);
      console.log('📅 Schedule data:', schedulesData.map(s => ({
        id: s.id,
        invoiceNumber: s.invoiceNumber,
        jobDate: s.jobDate,
        jobType: s.jobType,
        status: s.status
      })));
      setSchedules(schedulesData);
      setLoading(false);
    }, options);

    setSchedulesUnsubscribe(() => unsubscribe);
  }, [statusFilter]);



  // Load statistics
  const loadStats = useCallback(async () => {
    try {
      const statsData = await getScheduleStats();
      setStats(statsData);
    } catch (error) {
      console.error('Error loading schedule stats:', error);
    }
  }, []);

  // Auto-sync function
  const handleAutoSync = useCallback(async () => {
    if (autoSyncing || syncing) {
      console.log('🔄 Auto-sync skipped: Already syncing');
      return;
    }

    console.log('🔄 Starting auto-sync...');
    setAutoSyncing(true);
    setSyncStatus('syncing');

    try {
      const result = await syncSchedulesFromInvoices();
      console.log('✅ Auto-sync completed:', result);
      
      // Save sync timestamp
      saveLastSyncTime();
      setSyncStatus('success');
      
      // Refresh data
      loadStats();
      
      // Show subtle success message for auto-sync
      if (result.created > 0 || result.updated > 0) {
        message.success(`Auto-sync: ${result.created} created, ${result.updated} updated`, 3);
      }
    } catch (error) {
      console.error('❌ Auto-sync failed:', error);
      setSyncStatus('error');
      // Don't show error message for auto-sync to avoid disrupting user experience
    } finally {
      setAutoSyncing(false);
    }
  }, [autoSyncing, syncing, saveLastSyncTime, loadStats]);

  // Initialize sync state on mount
  useEffect(() => {
    const lastSync = getLastSyncTime();
    setLastSyncTime(lastSync);
    setSyncStatus(lastSync ? 'success' : 'never');
  }, [getLastSyncTime]);

  // Auto-sync trigger - check after schedules are loaded
  useEffect(() => {
    if (!loading && schedules.length !== undefined) {
      const shouldAutoSync = isSyncNeeded(schedules.length);
      if (shouldAutoSync) {
        console.log('🔄 Auto-sync would trigger, but temporarily disabled for debugging');
        // handleAutoSync(); // TEMPORARILY DISABLED FOR DEBUGGING
      }
    }
  }, [loading, schedules.length, isSyncNeeded, handleAutoSync]);

  // Initial load
  useEffect(() => {
    loadSchedules();
    loadStats();

    return () => {
      if (schedulesUnsubscribe) {
        schedulesUnsubscribe();
      }
    };
  }, [loadSchedules, loadStats]);

  // Filter schedules based on search and filters
  useEffect(() => {
    let filtered = schedules;

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(schedule =>
        schedule.customerName?.toLowerCase().includes(searchLower) ||
        schedule.companyName?.toLowerCase().includes(searchLower) ||
        schedule.contactPerson?.toLowerCase().includes(searchLower) ||
        schedule.phone?.toLowerCase().includes(searchLower) ||
        schedule.siteAddress?.toLowerCase().includes(searchLower) ||
        schedule.configuration?.toLowerCase().includes(searchLower) ||
        schedule.invoiceNumber?.toLowerCase().includes(searchLower)
      );
    }

    // Job type filter
    if (jobTypeFilter !== 'all') {
      console.log('🔍 Filtering by job type:', jobTypeFilter);
      console.log('🔍 Available job types in schedules:', [...new Set(schedules.map(s => s.jobType))]);
      filtered = filtered.filter(schedule => schedule.jobType === jobTypeFilter);
      console.log('🔍 Filtered schedules count:', filtered.length);
    }

    // Separate unconfirmed schedules from confirmed ones
    const unconfirmedFromSchedules = filtered.filter(schedule => schedule.status === SCHEDULE_STATUSES.UNCONFIRMED);
    const confirmedSchedules = filtered.filter(schedule => schedule.status !== SCHEDULE_STATUSES.UNCONFIRMED);

    setFilteredSchedules(confirmedSchedules);
    // Update unconfirmed schedules from actual schedule data instead of invoice data
    setUnconfirmedSchedules(unconfirmedFromSchedules);
  }, [schedules, searchTerm, jobTypeFilter]);

  // Sync schedules from invoices (manual sync)
  const handleSyncSchedules = async () => {
    setSyncing(true);
    setSyncStatus('syncing');
    try {
      const result = await syncSchedulesFromInvoices();
      message.success(`Manual sync completed: ${result.created} created, ${result.updated} updated`);
      
      // Save sync timestamp
      saveLastSyncTime();
      setSyncStatus('success');
      
      loadStats();
    } catch (error) {
      console.error('Error syncing schedules:', error);
      message.error('Failed to sync schedules from invoices');
      setSyncStatus('error');
    } finally {
      setSyncing(false);
    }
  };

  // Format last sync time for display
  const formatLastSyncTime = (timestamp) => {
    if (!timestamp) return 'Never';
    
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  // Get sync status color
  const getSyncStatusColor = () => {
    switch (syncStatus) {
      case 'syncing': return '#1890ff';
      case 'success': return '#52c41a';
      case 'error': return '#ff4d4f';
      default: return '#d9d9d9';
    }
  };

  // Get sync status text
  const getSyncStatusText = () => {
    switch (syncStatus) {
      case 'syncing': return autoSyncing ? 'Auto-syncing...' : 'Syncing...';
      case 'success': return 'Up to date';
      case 'error': return 'Sync failed';
      default: return 'Never synced';
    }
  };

  // Handle status change
  const handleStatusChange = async (scheduleId, newStatus) => {
    try {
      const success = await updateScheduleStatus(scheduleId, newStatus);
      if (success) {
        message.success('Schedule status updated');
        loadStats();
      } else {
        message.error('Failed to update schedule status');
      }
    } catch (error) {
      console.error('Error updating schedule status:', error);
      message.error('Failed to update schedule status');
    }
  };

  // Handle delete schedule
  const handleDeleteSchedule = async (scheduleId) => {
    Modal.confirm({
      title: 'Delete Schedule',
      content: 'Are you sure you want to delete this schedule?',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const success = await deleteSchedule(scheduleId);
          if (success) {
            message.success('Schedule deleted');
            loadStats();
          } else {
            message.error('Failed to delete schedule');
          }
        } catch (error) {
          console.error('Error deleting schedule:', error);
          message.error('Failed to delete schedule');
        }
      }
    });
  };

  // Handle edit schedule
  const handleEditSchedule = (schedule) => {
    setSelectedSchedule(schedule);
    setShowScheduleModal(true);
  };

  // Handle add new schedule
  const handleAddSchedule = () => {
    setSelectedSchedule(null);
    setShowScheduleModal(true);
  };

  // Handle schedule modal close
  const handleScheduleModalClose = () => {
    setShowScheduleModal(false);
    setSelectedSchedule(null);
    loadStats();
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return date.toLocaleDateString('en-SG', options);
  };


  // Format time for display
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    // timeStr is in HH:mm format, convert to 12-hour format
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Render schedule card in compact format
  const renderScheduleCard = (schedule) => {
    // Use standardized display date function
    const displayDate = getStandardizedDisplayDate(schedule);
    const displayTime = schedule.scheduledTime || schedule.jobTime;
    
    // Use standardized date checking functions
    const scheduleIsToday = isToday(displayDate);
    const scheduleIsPast = isPastDate(displayDate);
    const hasManualEdits = schedule.hasManualEdits || (schedule.editedFields && schedule.editedFields.length > 0);
    
    // Check if scheduled date differs from invoice date
    const hasScheduleChange = schedule.invoiceDate && schedule.scheduledDate && 
                             schedule.invoiceDate !== schedule.scheduledDate;

    return (
      <Card
        key={schedule.id}
        className={`schedule-card-compact ${scheduleIsToday ? 'today' : ''} ${scheduleIsPast ? 'past' : ''} ${hasManualEdits ? 'manually-edited' : ''}`}
        size="small"
        style={{ 
          marginBottom: 12,
          borderLeft: hasScheduleChange ? '4px solid #52c41a' : 
                     (hasManualEdits ? '4px solid #722ed1' : undefined)
        }}
      >
        <div className="schedule-content-compact">
          {/* Header with Date and Actions */}
          <div className="schedule-header-compact">
            <div className="schedule-date-info">
              <CalendarOutlined style={{ marginRight: 6, color: '#1890ff', fontSize: '14px' }} />
              <Text strong style={{ fontSize: '15px' }}>
                {formatDateShort(displayDate)}, {getDayName(displayDate)}
                {displayTime && ` • ${formatTime(displayTime)}`}
              </Text>
              <div className="schedule-tags">
                {scheduleIsToday && <Tag color="orange" size="small">TODAY</Tag>}
                {scheduleIsPast && <Tag color="default" size="small">PAST</Tag>}
                {hasScheduleChange && (
                  <Tooltip title={`Scheduled date changed from invoice date: ${schedule.invoiceDate}`}>
                    <Tag color="green" size="small">RESCHEDULED</Tag>
                  </Tooltip>
                )}
                {hasManualEdits && (
                  <Tooltip title={`Manually edited fields: ${(schedule.editedFields || []).join(', ')}`}>
                    <Tag color="purple" size="small">EDITED</Tag>
                  </Tooltip>
                )}
              </div>
            </div>
            
            {/* Desktop Actions */}
            <div className="schedule-actions-desktop">
              <Space size="small">
                {schedule.status === SCHEDULE_STATUSES.CONFIRMED && (
                  <Tooltip title="Mark Complete">
                    <Button
                      size="small"
                      type="primary"
                      icon={<CheckCircleOutlined />}
                      onClick={() => handleStatusChange(schedule.id, SCHEDULE_STATUSES.COMPLETED)}
                    />
                  </Tooltip>
                )}
                
                {schedule.status === SCHEDULE_STATUSES.COMPLETED && (
                  <Tooltip title="Mark Active">
                    <Button
                      size="small"
                      icon={<SyncOutlined />}
                      onClick={() => handleStatusChange(schedule.id, SCHEDULE_STATUSES.CONFIRMED)}
                    />
                  </Tooltip>
                )}

                <Tooltip title="Edit">
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => handleEditSchedule(schedule)}
                  />
                </Tooltip>

                {schedule.isManualEntry && (
                  <Tooltip title="Delete">
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteSchedule(schedule.id)}
                    />
                  </Tooltip>
                )}
              </Space>
            </div>
          </div>
          
          {/* Show invoice date if different from scheduled date */}
          {hasScheduleChange && (
            <div className="schedule-reschedule-note">
              Original invoice date: {schedule.invoiceDate}
            </div>
          )}

          {/* Main Content Grid */}
          <div className="schedule-details-compact">
            {/* Left Column */}
            <div className="schedule-left-column">
              <div className="schedule-info-row">
                <ToolOutlined style={{ color: '#52c41a', fontSize: '12px' }} />
                <Text strong style={{ fontSize: '13px' }}>JOB:</Text>
                <Text style={{ fontSize: '13px' }}>{schedule.jobType}</Text>
                <Tag 
                  color={SCHEDULE_STATUS_COLORS[schedule.status]} 
                  size="small"
                  style={{ marginLeft: 4 }}
                >
                  {SCHEDULE_STATUS_LABELS[schedule.status]}
                </Tag>
              </div>

              <div className="schedule-info-row">
                <UserOutlined style={{ color: '#722ed1', fontSize: '12px' }} />
                <Text strong style={{ fontSize: '13px' }}>CONTACT:</Text>
                <Text style={{ fontSize: '13px' }}>
                  {schedule.contactPerson} ({schedule.companyName})
                </Text>
              </div>

              <div className="schedule-info-row">
                <PhoneOutlined style={{ color: '#1890ff', fontSize: '12px' }} />
                <Text strong style={{ fontSize: '13px' }}>PHONE:</Text>
                <Text style={{ fontSize: '13px' }}>{schedule.phone}</Text>
              </div>
            </div>

            {/* Right Column */}
            <div className="schedule-right-column">
              <div className="schedule-info-row">
                <EnvironmentOutlined style={{ color: '#fa541c', fontSize: '12px' }} />
                <Text strong style={{ fontSize: '13px' }}>ADDRESS:</Text>
                <Text style={{ fontSize: '13px' }} className="address-text">
                  {schedule.siteAddress || 'Address not specified'}
                </Text>
              </div>

              <div className="schedule-info-row">
                <ToolOutlined style={{ color: '#13c2c2', fontSize: '12px' }} />
                <Text strong style={{ fontSize: '13px' }}>CONFIG:</Text>
                <Text style={{ fontSize: '13px' }} ellipsis={{ tooltip: true }}>
                  {schedule.configuration}
                </Text>
              </div>

              {schedule.invoiceNumber && (
                <div className="schedule-info-row">
                  <Text strong style={{ fontSize: '12px', color: '#666' }}>INVOICE:</Text>
                  <Text style={{ fontSize: '12px', color: '#666' }}>
                    {schedule.invoiceNumber}
                  </Text>
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          {schedule.notes && (
            <div className="schedule-notes">
              <Text type="secondary" style={{ fontSize: '12px', fontStyle: 'italic' }}>
                {schedule.notes}
              </Text>
            </div>
          )}

          {/* Mobile Actions */}
          <div className="schedule-actions-mobile">
            <Space size="small" wrap>
              {schedule.status === SCHEDULE_STATUSES.CONFIRMED && (
                <Button
                  size="small"
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleStatusChange(schedule.id, SCHEDULE_STATUSES.COMPLETED)}
                >
                  Complete
                </Button>
              )}
              
              {schedule.status === SCHEDULE_STATUSES.COMPLETED && (
                <Button
                  size="small"
                  icon={<SyncOutlined />}
                  onClick={() => handleStatusChange(schedule.id, SCHEDULE_STATUSES.CONFIRMED)}
                >
                  Reactivate
                </Button>
              )}

              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEditSchedule(schedule)}
              >
                Edit
              </Button>

              {schedule.isManualEntry && (
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteSchedule(schedule.id)}
                >
                  Delete
                </Button>
              )}
            </Space>
          </div>
        </div>
      </Card>
    );
  };

  // Render unconfirmed schedule card
  const renderUnconfirmedCard = (schedule) => {
    return (
      <Card
        key={schedule.id}
        className="unconfirmed-card"
        size="small"
        style={{ marginBottom: 16, borderColor: '#faad14' }}
      >
        <div className="schedule-content">
          <div className="schedule-date-header">
            <CalendarOutlined style={{ marginRight: 8, color: '#faad14' }} />
            <Text strong style={{ fontSize: '16px' }}>
              [Date To Be Confirmed]
            </Text>
            <Tag color="orange" style={{ marginLeft: 8 }}>UNCONFIRMED</Tag>
          </div>

          <div className="schedule-details">
            <div className="schedule-row">
              <ToolOutlined style={{ marginRight: 8, color: '#52c41a' }} />
              <Text strong>Job Type:</Text>
              <Text style={{ marginLeft: 8 }}>{schedule.jobType}</Text>
            </div>

            <div className="schedule-row">
              <UserOutlined style={{ marginRight: 8, color: '#722ed1' }} />
              <Text strong>Customer:</Text>
              <Text style={{ marginLeft: 8 }}>
                {schedule.contactPerson} ({schedule.companyName})
                <PhoneOutlined style={{ marginLeft: 8, marginRight: 4 }} />
                {schedule.phone}
              </Text>
            </div>

            <div className="schedule-row">
              <EnvironmentOutlined style={{ marginRight: 8, color: '#fa541c' }} />
              <Text strong>Site Address:</Text>
              <Text style={{ marginLeft: 8 }}>{schedule.siteAddress || 'Address not specified'}</Text>
            </div>

            <div className="schedule-row">
              <Text strong>Invoice:</Text>
              <Text style={{ marginLeft: 8 }}>{schedule.invoiceNumber}</Text>
            </div>

            {schedule.notes && (
              <div className="schedule-row">
                <Text type="secondary" style={{ fontSize: '12px', fontStyle: 'italic' }}>
                  {schedule.notes}
                </Text>
              </div>
            )}
          </div>

          {/* Actions for unconfirmed schedules */}
          <div className="schedule-actions-mobile" style={{ marginTop: 12 }}>
            <Space size="small" wrap>
              <Button
                size="small"
                type="primary"
                icon={<EditOutlined />}
                onClick={() => handleEditSchedule(schedule)}
              >
                Set Date & Time
              </Button>
            </Space>
          </div>
        </div>
      </Card>
    );
  };

  if (loading) {
    return <div className="loading">Loading job schedules...</div>;
  }

  return (
    <div className="job-schedule-list">
      {/* Header */}
      <div className="section-header-redesigned">
        <div className="header-top">
          <div className="header-left">
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Title level={2}>Job Schedule Management</Title>
              {/* Sync Status Indicator */}
              <Tooltip title={`Last synced: ${formatLastSyncTime(lastSyncTime)}`}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '6px',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  backgroundColor: '#f5f5f5',
                  fontSize: '12px'
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: getSyncStatusColor()
                  }} />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {getSyncStatusText()}
                  </Text>
                </div>
              </Tooltip>
            </div>
          </div>
          
          {/* Desktop Add Button */}
          <div className="header-right desktop-only">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddSchedule}
              size="large"
            >
              Add Manual Schedule
            </Button>
          </div>
        </div>
        
        {/* Statistics */}
        <div className="stats-summary">
          <div className="stat-item">
            <span className="stat-number" style={{color: '#1890ff'}}>{stats.upcoming || 0}</span>
            <span className="stat-label">Upcoming Jobs</span>
          </div>
          <div className="stat-item">
            <span className="stat-number" style={{color: '#52c41a'}}>{stats.today || 0}</span>
            <span className="stat-label">Today</span>
          </div>
          <div className="stat-item">
            <span className="stat-number" style={{color: '#722ed1'}}>{stats.tomorrow || 0}</span>
            <span className="stat-label">Tomorrow</span>
          </div>
          <div className="stat-item">
            <span className="stat-number" style={{color: '#13c2c2'}}>{stats.thisWeek || 0}</span>
            <span className="stat-label">This Week</span>
          </div>
          <div className="stat-item">
            <span className="stat-number" style={{color: '#faad14'}}>{stats.unconfirmed || 0}</span>
            <span className="stat-label">Unconfirmed</span>
          </div>
          {(stats.completed > 0 || stats.past > 0) && (
            <div className="stat-item">
              <span className="stat-number" style={{color: '#8c8c8c'}}>{(stats.completed || 0) + (stats.past || 0)}</span>
              <span className="stat-label">Completed</span>
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <Row gutter={[16, 16]}>
          {/* Search Input - Full width on mobile, half width on tablet+ */}
          <Col xs={24} sm={24} md={12} lg={8} xl={8}>
            <Input
              placeholder="Search schedules..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="large"
              className="mobile-search-input"
            />
          </Col>
          
          {/* Status Filter - Half width on mobile, quarter width on desktop */}
          <Col xs={12} sm={12} md={6} lg={4} xl={4}>
            <Select
              placeholder="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: '100%' }}
              size="large"
              className="mobile-filter-select"
            >
              <Option value="active">Active Jobs</Option>
              <Option value="completed">Completed</Option>
              <Option value="all">All Jobs</Option>
            </Select>
          </Col>
          
          {/* Job Type Filter - Half width on mobile, quarter width on desktop */}
          <Col xs={12} sm={12} md={6} lg={4} xl={4}>
            <Select
              placeholder="Job Type"
              value={jobTypeFilter}
              onChange={setJobTypeFilter}
              style={{ width: '100%' }}
              size="large"
              className="mobile-filter-select"
            >
              <Option value="all">All Job Types</Option>
              {Object.entries(JOB_TYPE_LABELS).map(([key, label]) => (
                <Option key={key} value={key}>{label}</Option>
              ))}
            </Select>
          </Col>
          
          {/* Action Buttons - Full width on mobile, right-aligned on desktop */}
          <Col xs={24} sm={24} md={24} lg={8} xl={8}>
            <div className="filter-actions">
              <Space wrap size="small" className="mobile-button-group">
                <Tooltip title={autoSyncing ? "Auto-sync in progress..." : "Manual sync from invoices"}>
                  <Button
                    icon={<SyncOutlined spin={syncing || autoSyncing} />}
                    loading={syncing}
                    disabled={autoSyncing}
                    onClick={handleSyncSchedules}
                    size="large"
                    className="mobile-action-button"
                  >
                    <span className="button-text-desktop">{autoSyncing ? 'Auto-syncing...' : 'Sync from Invoices'}</span>
                    <span className="button-text-mobile">Sync</span>
                  </Button>
                </Tooltip>
                {/* Mobile Add Button */}
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddSchedule}
                  size="large"
                  className="mobile-action-button mobile-only"
                >
                  <span className="button-text-desktop">Add Manual Schedule</span>
                  <span className="button-text-mobile">Add Schedule</span>
                </Button>
              </Space>
            </div>
          </Col>
        </Row>
      </div>

      {/* Schedules List */}
      <div className="schedules-content">
        {filteredSchedules.length === 0 && unconfirmedSchedules.length === 0 ? (
          <Empty
            description="No schedules found"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button type="primary" onClick={handleAddSchedule}>
              Add First Schedule
            </Button>
          </Empty>
        ) : (
          <>
            {/* Confirmed Schedules */}
            {filteredSchedules.length > 0 && (
              <div className="confirmed-schedules">
                <Title level={4}>
                  {statusFilter === 'completed' ? 'Completed Jobs' : 
                   statusFilter === 'all' ? 'All Jobs' : 'Upcoming Jobs'}
                  ({filteredSchedules.length})
                </Title>
                {filteredSchedules.map(renderScheduleCard)}
              </div>
            )}

            {/* Unconfirmed Schedules */}
            {unconfirmedSchedules.length > 0 && statusFilter !== 'completed' && (
              <>
                <Divider />
                <div className="unconfirmed-schedules">
                  <Title level={4} style={{ color: '#faad14' }}>
                    Unconfirmed Schedules ({unconfirmedSchedules.length})
                  </Title>
                  <Text type="secondary" style={{ marginBottom: 16, display: 'block' }}>
                    These schedules have dates to be confirmed. Click "Set Date & Time" to schedule them.
                  </Text>
                  {unconfirmedSchedules.map(renderUnconfirmedCard)}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Schedule Form Modal */}
      {showScheduleModal && (
        <Modal
          title={selectedSchedule ? 'Edit Schedule' : 'Add New Schedule'}
          open={showScheduleModal}
          onCancel={handleScheduleModalClose}
          footer={null}
          width={800}
          destroyOnClose
        >
          <JobScheduleForm
            schedule={selectedSchedule}
            onSave={handleScheduleModalClose}
            onCancel={handleScheduleModalClose}
          />
        </Modal>
      )}
    </div>
  );
};

export default JobScheduleList;
